import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	DEFAULT_SEND_ERROR_RANGE,
	SendErrorRange,
	SupportCompaniesResponse,
	SupportCompany,
	SupportSendError,
	SupportSendErrorsOverview,
	SupportUser,
	SupportUsersResponse,
} from "../../pages/SupportDashboard/schema/support-dashboard";

import { supportDashboardApi } from "./support-dashboard.api";

interface SupportDashboardState {
	companies: SupportCompany[];
	meta: SupportCompaniesResponse["meta"] | null;
	/** PREC-4336 part C: the last completed rollup day the activity figures end on. */
	lastRolledUpDate: string | null;
	users: SupportUser[];
	usersMeta: SupportUsersResponse["meta"] | null;
	sendErrorRange: SendErrorRange;
	sendErrorsOverview: SupportSendErrorsOverview | null;
	sendErrorsByCompany: Record<number, SupportSendError[]>;
}

const initialState: SupportDashboardState = {
	companies: [],
	meta: null,
	lastRolledUpDate: null,
	users: [],
	usersMeta: null,
	sendErrorRange: DEFAULT_SEND_ERROR_RANGE,
	sendErrorsOverview: null,
	sendErrorsByCompany: {},
};

export const supportDashboardSlice = createSlice({
	name: "supportDashboard",
	initialState,
	reducers: {
		setSendErrorRange: (state, { payload }: PayloadAction<SendErrorRange>) => {
			state.sendErrorRange = payload;
			// Cached per-company detail is range-scoped — drop it so panels refetch.
			state.sendErrorsByCompany = {};
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			supportDashboardApi.endpoints.getSupportCompanies.matchFulfilled,
			(state, { payload }) => {
				state.companies = payload.data;
				state.meta = payload.meta;
				state.lastRolledUpDate = payload.lastRolledUpDate;
			}
		);
		builder.addMatcher(
			supportDashboardApi.endpoints.getSupportUsers.matchFulfilled,
			(state, { payload }) => {
				state.users = payload.data;
				state.usersMeta = payload.meta;
			}
		);
		builder.addMatcher(
			supportDashboardApi.endpoints.getSendErrorsOverview.matchFulfilled,
			(state, { payload }) => {
				state.sendErrorsOverview = payload;
			}
		);
		builder.addMatcher(
			supportDashboardApi.endpoints.getCompanySendErrors.matchFulfilled,
			(state, { payload, meta }) => {
				state.sendErrorsByCompany[meta.arg.originalArgs.companyId] = payload;
			}
		);
	},
});

export const { setSendErrorRange } = supportDashboardSlice.actions;
export const supportDashboardReducer = supportDashboardSlice.reducer;
export const supportDashboardSelector = (state: RootState) => state[supportDashboardSlice.name];
